import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Home, 
  Car, 
  Plus, 
  FileText, 
  History, 
  Users, 
  ClipboardList, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import useActivityTracker from "../hooks/useActivityTracker";

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const { logout, isAdmin, user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Initialize activity tracking
  useActivityTracker();

  // Auto-collapse on mobile
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) { // lg breakpoint
        setIsCollapsed(true);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const baseMenuItems = [
    { name: "Home", path: "/home", icon: Home },
    { name: "Vehicle List", path: "/vehiclelist", icon: Car },
    { name: "Add Vehicle", path: "/add-vehicle", icon: Plus },
    { name: "Projects", path: "/projects", icon: FileText },
    { name: "Add Project", path: "/add-project", icon: Plus },
    { name: "Change Logs", path: "/logs", icon: History },
  ];

  const menuItems = isAdmin()
    ? [
        ...baseMenuItems, 
        { name: "User Management", path: "/user-management", icon: Users }, 
        { name: "Activity Logs", path: "/activity-logs", icon: ClipboardList }
      ]
    : baseMenuItems;



  return (
    <div className="flex min-h-screen">
      {/* Collapsible Sidebar */}
      <aside className={`${
        isCollapsed ? 'w-16' : 'w-64'
      } bg-gray-900 text-white transition-all duration-300 ease-in-out flex flex-col h-screen sticky top-0 shrink-0 z-20`}>
        
        {/* Header with Toggle Button */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          {!isCollapsed && (
            <div className="space-y-0">
            <h1 className="text-lg font-bold leading-tight">Fleet Management</h1>
            <h1 className="text-lg font-bold leading-tight">System</h1>
            </div>
          )}
          <Button
            onClick={() => setIsCollapsed(!isCollapsed)}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-gray-700 p-1 h-8 w-8 transition-all"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation Menu */}
        <nav className="space-y-2 flex-1 overflow-y-auto p-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-700 transition-colors ${
                location.pathname === item.path ? "bg-gray-700" : ""
              }`}
              title={isCollapsed ? item.name : ""}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && (
                <span className="truncate">{item.name}</span>
              )}
            </Link>
          ))}
        </nav>

        {/* Footer with User Info and Logout */}
        <div className="p-2 border-t border-gray-700">
          {/* User Info */}
          {user && (
            <div className={`mb-3 px-3 py-2 bg-gray-800 rounded-lg text-center ${
              isCollapsed ? 'px-2' : ''
            }`}>
              {!isCollapsed ? (
                <>
                  <div className="text-sm text-gray-300">Logged in as</div>
                  <div className="text-white font-medium truncate">{user.username}</div>
                </>
              ) : (
                <div className="text-white font-medium text-lg">
                  {user.username.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}
          
          {/* Logout Button */}
          <Button 
            onClick={logout} 
            variant="outline" 
            className={`w-full bg-gray-800 text-white hover:bg-gray-700 ${
              isCollapsed ? 'px-2' : ''
            }`}
            title={isCollapsed ? "Logout" : ""}
          >
            <LogOut className="w-5 h-5" />
            {!isCollapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 bg-gray-100">
        {/* Mobile Toggle Button */}
        {/*<div className="lg:hidden p-4 border-b border-gray-200 bg-white">
          <Button
            onClick={() => setIsCollapsed(!isCollapsed)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Menu className="w-4 h-4" />
            {isCollapsed ? "Show Menu" : "Hide Menu"}
          </Button>
        </div>*/}
        
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
